import React, { useState, useEffect } from "react"
import * as layouts from "layouts"
import * as inputs from "inputs"
import * as errors from "errors"
import * as brands from "brands"
import * as authzc from "authzcached"
import * as httpx from "httpx"
import * as api from "brandguard/api"
import * as uuid from "uuid"
import * as training from "brandguard/display/training"
import * as debugx from "x/debugx"
import * as bgscores from "brandguard/display/scores"
import { DocumentKernelSetting } from "brandguard/brandguard.kernel_pb"
import LogoDetection from "./settings.logo.detection"
import FontDetection from "./settings.font.detection"
import BackgroundColors from "./settings.background.colors"
import LookAndFeel from "./settings.lookandfeel"
import CustomRules from "./settings.custom.rules"
import ProductTaxonomy from "./settings.product.taxonomy"

interface props extends layouts.containers.FlexProps {
	current: DocumentKernelSetting
	onChange(upd: DocumentKernelSetting): void
	onSave?(): void
	refreshTrigger?: number
}

export default function Settings(props: React.PropsWithChildren<props>): JSX.Element {
	const { current, onChange, onSave, refreshTrigger, ...rest } = props
	const brand = brands.caching.useCached()
	const authzaccount = authzc.useCache((cached) => cached.meta)
	const [cause, setCause] = useState(undefined as errors.Cause)
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		setLoading(true)
		const retry = httpx.autoretry()
		const p = retry
			.wrap(() => api.documents.kernel.config.latest(brand.id, brand.id, authzc.bearer(authzaccount)))
			.then((r) => {
				onChange(api.documents.kernel.config.zero({ ...r.settings!, id: uuid.v4() }))
				setLoading(false)
				setCause(undefined)
			})
			.catch(
				httpx.errors.notFound((cause) => {
					console.debug("unable to find document kernel", cause)
					setLoading(false)
					setCause(undefined)
				}),
			)
			.catch((cause) => {
				console.warn(cause)
				setCause(
					<errors.Display cause={cause} onClick={() => setCause(undefined)}>
						failed to load settings
					</errors.Display>,
				)
				setLoading(false)
			})
		return p.cancel
	}, [refreshTrigger])

	return (
		<layouts.Flex flexDirection="column" flex="1" color={layouts.theme.colors.grey.dark10} {...rest}>
			<layouts.Flex my="20px" justifyContent="end">
				<training.layouts.CopyConfigFromAssetType
					dstType="document"
					brand={brand}
					current={current}
					onChange={onChange}
					setLoading={setLoading}
					setCause={setCause}
					httpOptions={authzc.bearer(authzaccount)}
				/>
				<layouts.buttons.primary
					width="10ch"
					borderRadius="5px"
					onClick={() => {
						setLoading(true)
						const retry = httpx.autoretry()
						retry
							.wrap(() =>
								api.documents.kernel.create_version(
									brand.id,
									brand.id,
									{ settings: current },
									authzc.bearer(authzaccount),
								),
							)
							.then((r) => {
								onChange({ ...current, id: uuid.v4() })
								setLoading(false)
								setCause(undefined)
								onSave?.()
							})
							.catch((cause) => {
								console.warn(cause)
								setCause(
									<errors.Display cause={cause} onClick={() => setCause(undefined)}>
										failed to update settings
									</errors.Display>,
								)
								setLoading(false)
							})
					}}
				>
					Save
				</layouts.buttons.primary>
			</layouts.Flex>
			<layouts.forms.Container key={current.id} loading={loading} cause={cause}>
				{debugx.alpha.enabled() && (
					<training.layouts.styledFormContainer padding="5px 10px">
						<training.layouts.styledFormLabel>Threshold</training.layouts.styledFormLabel>
						<inputs.Numeric
							defaultValue={current.threshold}
							onChange={(evt) =>
								onChange({
									...current,
									threshold: evt.target.valueAsNumber,
								})
							}
							className={training.layouts.styledInput}
							style={{
								marginLeft: "10px",
								maxWidth: "40px",
								background: layouts.theme.backgrounds.bluealpha,
								border: `1px solid ${layouts.theme.colors.grey.dark50alpha10}`,
								minHeight: "30px",
							}}
						/>
					</training.layouts.styledFormContainer>
				)}
				<training.layouts.styledFormContainer>
					<training.layouts.styledFormLabel>Brand Consistency</training.layouts.styledFormLabel>
					<inputs.toggles.Display
						ml="auto"
						mr="20px"
						checked={current.brand_consistency!.enabled}
						onClick={() =>
							onChange({
								...current,
								brand_consistency: { ...current.brand_consistency, enabled: !current.brand_consistency!.enabled },
							})
						}
						className={training.layouts.styledToggle}
					/>
					<layouts.Flex width="14px" />
				</training.layouts.styledFormContainer>
				<training.layouts.styledFormContainer>
					<training.layouts.styledFormLabel>Violence Check</training.layouts.styledFormLabel>
					<inputs.toggles.Display
						ml="auto"
						mr="20px"
						checked={current.doc_violence!.enabled}
						onClick={() =>
							onChange({
								...current,
								doc_violence: { ...current.doc_violence, enabled: !current.doc_violence!.enabled },
							})
						}
						className={training.layouts.styledToggle}
					/>
					<layouts.Flex width="14px" />
				</training.layouts.styledFormContainer>
				<training.layouts.styledFormContainer>
					<training.layouts.styledFormLabel>Nudity Check</training.layouts.styledFormLabel>
					<inputs.toggles.Display
						ml="auto"
						mr="20px"
						checked={current.doc_nudity!.enabled}
						onClick={() =>
							onChange({
								...current,
								doc_nudity: { ...current.doc_nudity!, enabled: !current.doc_nudity!.enabled },
							})
						}
						className={training.layouts.styledToggle}
					/>
					<layouts.Flex width="14px" />
				</training.layouts.styledFormContainer>
				<training.layouts.styledFormContainer>
					<training.layouts.styledFormLabel>AI Generated</training.layouts.styledFormLabel>
					<inputs.toggles.Display
						ml="auto"
						mr="20px"
						checked={current.ai_generated?.enabled ?? false}
						onClick={() =>
							onChange({
								...current,
								ai_generated: { ...current.ai_generated, enabled: !(current.ai_generated?.enabled ?? false) },
							})
						}
						className={training.layouts.styledToggle}
					/>
					<layouts.Flex width="14px" />
				</training.layouts.styledFormContainer>
				<training.layouts.styledFormContainer>
					<training.layouts.styledFormLabel>Racism Check</training.layouts.styledFormLabel>
					<inputs.toggles.Display
						ml="auto"
						mr="20px"
						checked={current.doc_racism!.enabled}
						onClick={() =>
							onChange({
								...current,
								doc_racism: { ...current.doc_racism!, enabled: !current.doc_racism!.enabled },
							})
						}
						className={training.layouts.styledToggle}
					/>
					<layouts.Flex width="14px" />
				</training.layouts.styledFormContainer>
				<training.layouts.styledFormContainer>
					<training.layouts.styledFormLabel>Grammar Check</training.layouts.styledFormLabel>
					<inputs.toggles.Display
						ml="auto"
						mr="20px"
						checked={current.doc_grammar!.enabled}
						onClick={() =>
							onChange({
								...current,
								doc_grammar: { ...current.doc_grammar!, enabled: !current.doc_grammar!.enabled },
							})
						}
						className={training.layouts.styledToggle}
					/>
					<layouts.Flex width="14px" />
				</training.layouts.styledFormContainer>
				<layouts.accordions.Container className={training.layouts.styledAccordion}>
					<layouts.accordions.Description>
						<training.layouts.styledFormLabel>Logo Detection</training.layouts.styledFormLabel>
						<inputs.toggles.Display
							ml="auto"
							mr="20px"
							checked={current.doc_logo_detection!.enabled}
							onClick={() => {
								onChange({
									...current,
									doc_logo_detection: {
										...current.doc_logo_detection!,
										enabled: !current.doc_logo_detection!.enabled,
									},
								})
							}}
							className={training.layouts.styledToggle}
						/>
					</layouts.accordions.Description>
					<layouts.accordions.Content>
						<layouts.Flex flexDirection="column">
							<layouts.Flex my="5px">
								<bgscores.layouts.accordion.subheader>
									To train your brand&apos;s scoring system on its logos, contact us at{" "}
									<a href={"mailto:developers@brandguard.ai"}>developers@brandguard.ai</a>. Please include your logo
									files in the email.
								</bgscores.layouts.accordion.subheader>
							</layouts.Flex>
							{debugx.alpha.enabled() && (
								<LogoDetection
									settings={current.doc_logo_detection!}
									onChange={(upd) => {
										onChange({ ...current, doc_logo_detection: upd })
									}}
								/>
							)}
						</layouts.Flex>
					</layouts.accordions.Content>
				</layouts.accordions.Container>
				<layouts.accordions.Container className={training.layouts.styledAccordion}>
					<layouts.accordions.Description>
						<training.layouts.styledFormLabel>Font Detection</training.layouts.styledFormLabel>
						<inputs.toggles.Display
							ml="auto"
							mr="20px"
							checked={current.doc_font_detection!.enabled}
							onClick={() => {
								onChange({
									...current,
									doc_font_detection: {
										...current.doc_font_detection!,
										enabled: !current.doc_font_detection!.enabled,
									},
								})
							}}
							className={training.layouts.styledToggle}
						/>
					</layouts.accordions.Description>
					<layouts.accordions.Content>
						<layouts.Flex flexDirection="column">
							<layouts.Flex my="5px">
								<bgscores.layouts.accordion.subheader>
									To train your brand&apos;s scoring system on its fonts, contact us at{" "}
									<a href={"mailto:developers@brandguard.ai"}>developers@brandguard.ai</a>. Please include your font
									files in the email.
								</bgscores.layouts.accordion.subheader>
							</layouts.Flex>
							{debugx.alpha.enabled() && (
								<FontDetection
									settings={current.doc_font_detection!}
									onChange={(upd) => {
										onChange({ ...current, doc_font_detection: upd })
									}}
								/>
							)}
						</layouts.Flex>
					</layouts.accordions.Content>
				</layouts.accordions.Container>
				<layouts.accordions.Container className={training.layouts.styledAccordion}>
					<layouts.accordions.Description>
						<training.layouts.styledFormLabel>Colors</training.layouts.styledFormLabel>
						<inputs.toggles.Display
							ml="auto"
							mr="20px"
							checked={current.doc_background_colors!.enabled}
							onClick={() => {
								onChange({
									...current,
									doc_background_colors: {
										...current.doc_background_colors!,
										enabled: !current.doc_background_colors!.enabled,
									},
								})
							}}
							className={training.layouts.styledToggle}
						/>
					</layouts.accordions.Description>
					<layouts.accordions.Content>
						<BackgroundColors
							settings={current.doc_background_colors!}
							onChange={(upd) => {
								onChange({ ...current, doc_background_colors: upd })
							}}
						/>
					</layouts.accordions.Content>
				</layouts.accordions.Container>
				<layouts.accordions.Container className={training.layouts.styledAccordion}>
					<layouts.accordions.Description>
						<training.layouts.styledFormLabel>Look & Feel</training.layouts.styledFormLabel>
						<inputs.toggles.Display
							ml="auto"
							mr="20px"
							checked={current.doc_lookandfeel!.enabled}
							onClick={() => {
								onChange({
									...current,
									doc_lookandfeel: {
										...current.doc_lookandfeel!,
										enabled: !current.doc_lookandfeel!.enabled,
									},
								})
							}}
							className={training.layouts.styledToggle}
						/>
					</layouts.accordions.Description>
					<layouts.accordions.Content>
						<LookAndFeel
							settings={current.doc_lookandfeel!}
							onChange={(upd) => {
								onChange({ ...current, doc_lookandfeel: upd })
							}}
						/>
					</layouts.accordions.Content>
				</layouts.accordions.Container>
				<layouts.accordions.Container className={training.layouts.styledAccordion}>
					<layouts.accordions.Description>
						<training.layouts.styledFormLabel>Custom Rules</training.layouts.styledFormLabel>
						<inputs.toggles.Display
							ml="auto"
							mr="20px"
							checked={current.doc_rules!.enabled}
							onClick={() => {
								onChange({
									...current,
									doc_rules: {
										...current.doc_rules!,
										enabled: !current.doc_rules!.enabled,
									},
								})
							}}
							className={training.layouts.styledToggle}
						/>
					</layouts.accordions.Description>
					<layouts.accordions.Content>
						<CustomRules
							settings={current.doc_rules!}
							onChange={(upd) => {
								onChange({ ...current, doc_rules: upd })
							}}
						/>
					</layouts.accordions.Content>
				</layouts.accordions.Container>
				<layouts.accordions.Container className={training.layouts.styledAccordion}>
					<layouts.accordions.Description>
						<training.layouts.styledFormLabel>Product Taxonomy</training.layouts.styledFormLabel>
						<inputs.toggles.Display
							ml="auto"
							mr="20px"
							checked={current.product_detection!.enabled}
							onClick={() => {
								onChange({
									...current,
									product_detection: {
										...current.product_detection!,
										enabled: !current.product_detection!.enabled,
									},
								})
							}}
							className={training.layouts.styledToggle}
						/>
					</layouts.accordions.Description>
					<layouts.accordions.Content>
						<layouts.Flex flexDirection="column">
							<layouts.Flex my="5px">
								<bgscores.layouts.accordion.subheader>
									Define products and technologies for detection. Detected products will be shown in
									explanations but do not affect approval verdicts.
								</bgscores.layouts.accordion.subheader>
							</layouts.Flex>
							<ProductTaxonomy refreshTrigger={refreshTrigger} />
						</layouts.Flex>
					</layouts.accordions.Content>
				</layouts.accordions.Container>
			</layouts.forms.Container>
		</layouts.Flex>
	)
}
